/* eslint-disable jsx-a11y/img-redundant-alt */
import React, { useState, useMemo, useEffect } from 'react';
import type { UploadFile, RcFile } from 'antd/es/upload/interface';
import classNames from 'classnames';
import { toast } from 'react-toastify';

import {
  trackDemoAddFile,
  trackDemoUploadFileSuccess,
  trackDemoUploadFileError,
  trackDemoBatchUploadVideoSuccess,
  trackDemoAnalysisStartSuccess,
  trackDemoAnalysisStartError,
} from '../../../analytics';
import landscapeOverlay from '../../../assets/landscape-overlay.png';

import { FancyBtn } from '../../../components/custom';
import { Button, Alert } from '../../../components/general';
import { SideIcon } from '../../../components/icons';
import { ContentHeader } from '../../../components/layout/content';

import useDemoData from '../../../hooks/useDemoData';
import { useWindowWidth } from '../../../hooks/useWindowWidth';

import { analyseAll } from '../../../services/api/analyse';
import {
  getAttendeeFacePhotos,
  getClipFiles,
} from '../../../services/api/files';
import {
  updatePersonPhoto,
  updatePersonVideos,
} from '../../../services/checkInService';
import { sendSlackDemoNotification } from '../../../services/slackService';

import { PublicEvent, File, ModalError } from '../../../types';
import {
  isEmpty,
  getFileImg,
  getFileUrl,
  getBase64,
  getBpWidth,
} from '../../../utils';

import { getTipsList } from '../../CheckIn/components/getCheckinText';
import PageSpinner from '../../General/pageSpinner';
import DemoLoading from '../components/demoLoading';
import DemoUploader from '../components/demoUploader';

interface Props {
  event: PublicEvent;
  nextStep: () => void;
  setError: React.Dispatch<React.SetStateAction<ModalError>>;
}

const UploadStep: React.FC<Props> = (props) => {
  const { event, nextStep, setError } = props;

  const { demoData, updateDemoData } = useDemoData();

  const windowWidth = useWindowWidth();
  const mobilePrefix = useMemo(() => {
    return windowWidth < getBpWidth('sm') ? 'mobile_' : '';
  }, [windowWidth]);

  const [photoFile, setPhotoFile] = useState<RcFile | null>(null);
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  const [uploadedPhotos, setUploadedPhotos] = useState<File[]>([]);

  const [videoFiles, setVideoFiles] = useState<RcFile[]>([]);
  const [uploadedVideos, setUploadedVideos] = useState<File[]>([]);

  const [stage, setStage] = useState('photo');

  const [isLoading, setIsLoading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    const onMount = async () => {
      setIsLoading(true);

      const photos = (
        await getAttendeeFacePhotos(event.eventId, demoData.user.elementId)
      ).filter((photo: File) => demoData.photos.includes(photo.elementId));
      const clips = (await getClipFiles(event.eventId, true)).filter(
        (clip: File) => demoData.clips.includes(clip.elementId),
      );

      setUploadedPhotos(photos);
      setUploadedVideos(clips);

      setIsLoading(false);
      setStage('summary');
    };

    if (!isEmpty(demoData.photos) && !isEmpty(demoData.clips)) onMount();

    updateDemoData({ ...demoData, status: 'upload' });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event.eventId]);

  useEffect(() => {
    if (stage === 'video')
      toast(
        <div className="text-sm">
          <b>Demo note</b>: In real projects, CrowdClip&reg; analyses full event
          footage libraries and generates personalised videos for many
          attendees.
          <br />
          <br />
          For this demo, we use two short videos so you can see the core
          workflow quickly.
        </div>,
        { position: 'bottom-right', autoClose: false },
      );
  }, [stage]);

  const onChangeImg = (file: RcFile) => {
    getBase64(file, (url: string) => setPhotoUrl(url));
    setPhotoFile(file);

    if (hasUploaded) setStage('summary');
    else setStage('video');

    trackDemoAddFile('photo');
  };

  const onChangeVideo = (file: RcFile) => {
    setVideoFiles((prev) => [...prev, file]);
    setStage('summary');

    trackDemoAddFile('video');
  };

  const handleDeleteVideo = (uid: string) => {
    setVideoFiles((prev) => prev.filter((v) => v.uid !== uid));
  };

  const handleSubmit = async () => {
    setIsProcessing(true);

    try {
      let uploadedPhotos: string[] = [];
      let uploadedVideos: string[] = [];

      if (photoFile) {
        const result = await updatePersonPhoto({
          eventId: event.eventId,
          parentElementId: demoData.user.elementId,
          photo: photoFile as UploadFile,
          trackOnSuccess: () => trackDemoUploadFileSuccess('photo'),
          trackOnError: (fileName: string) =>
            trackDemoUploadFileError('photo', fileName),
        });
        uploadedPhotos = result.uploadedPhotos;
      }

      if (!isEmpty(videoFiles)) {
        const result = await updatePersonVideos({
          eventId: event.eventId,
          videos: videoFiles,
          trackOnSuccess: () => trackDemoUploadFileSuccess('video'),
          trackOnError: (fileName: string) =>
            trackDemoUploadFileError('video', fileName),
          trackOnBatchSuccess: (videoCount: number) =>
            trackDemoBatchUploadVideoSuccess(videoCount),
        });
        uploadedVideos = result.uploadedVideos;
      }

      updateDemoData({
        ...demoData,
        photos: [...demoData.photos, ...uploadedPhotos],
        clips: [...demoData.clips, ...uploadedVideos],
      });

      try {
        await analyseAll(event.eventId, true);

        trackDemoAnalysisStartSuccess();
        await sendSlackDemoNotification({
          action: 'Started Upload & Analysis',
          user: demoData.user,
          details: `Uploaded ${uploadedPhotos.length}/${photoFile ? 1 : 0} photo and ${uploadedVideos.length}/${videoFiles.length} video(s)`,
        });

        nextStep();
      } catch (err: any) {
        trackDemoAnalysisStartError(err.message);
        throw err;
      }
    } catch (err: any) {
      await sendSlackDemoNotification({
        action: 'Started Upload & Analysis',
        user: demoData.user,
        details: err.message,
        isError: true,
      });
      setError({ subheading: err.message });
    } finally {
      setIsProcessing(false);
    }
  };

  const totalSize = useMemo(() => {
    return videoFiles.reduce((acc, file) => acc + file.size / (1024 * 1024), 0);
  }, [videoFiles]);

  const { hasUploaded, hasToUpload } = useMemo(() => {
    return {
      hasUploaded: !isEmpty(uploadedPhotos) || !isEmpty(uploadedVideos),
      hasToUpload: !!photoUrl || !isEmpty(videoFiles),
    };
  }, [photoUrl, videoFiles, uploadedPhotos, uploadedVideos]);

  if (isLoading) return <PageSpinner noTips />;

  if (isProcessing)
    return (
      <DemoLoading
        heading="Uploading..."
        tipsList={getTipsList()}
        expectedMin={totalSize / 100 + 1} // estimate 1 min per 100 MB (+ 1 min for buffer)
      />
    );

  if (stage === 'photo')
    return (
      <React.Fragment>
        <ContentHeader title="Upload your photo" />

        <div className="text-lg">
          Upload a clear selfie so CrowdClip&reg; can match you in the footage.
        </div>

        <div className="w-full h-fit flex flex-col sm:flex-row items-center gap-4 sm:gap-8">
          <DemoUploader type="photo" onDrop={onChangeImg} />

          <div className="w-full sm:w-1/3 flex flex-col gap-2">
            <div className="font-semibold">Example</div>

            <div className="flex flex-col items-center gap-1 text-sm">
              <img
                src={require(`../../../assets/demo/${mobilePrefix}photo.jpg`)}
                alt="Example Photo"
                className="w-1/2 sm:w-full aspect-square rounded-md object-cover"
              />
              <div>Clear close-up of a face</div>
            </div>
          </div>
        </div>

        <div className="text-sm">
          Privacy: Your photo and videos are used only to run this demo (to
          detect your appearances and generate your reel). We don’t sell or
          share your data, and we delete uploads after processing.
        </div>
      </React.Fragment>
    );

  if (stage === 'video')
    return (
      <React.Fragment>
        <ContentHeader title="Upload your videos" />

        <div className="text-lg">
          Add <b>two short portrait videos</b> (5 seconds or longer): one where
          you appear, and one video where you don’t.
          <br />
          Our AI will scan the videos, find you, and use that clip in the final
          video.
        </div>

        <div className="w-full h-fit flex flex-col sm:flex-row items-center gap-4 sm:gap-8">
          <DemoUploader type="video" onDrop={onChangeVideo} />

          <div className="w-full sm:w-1/3 flex flex-col gap-2">
            <div className="font-semibold">Example</div>

            <div className="flex gap-4 text-sm">
              {['with', 'without'].map((type) => (
                <div key={type} className="w-1/2 flex flex-col gap-1">
                  <video
                    src={require(
                      `../../../assets/demo/${mobilePrefix}video-${type}.mp4`,
                    )}
                    className="aspect-[9/16] rounded-md"
                    autoPlay
                    muted
                    loop
                    playsInline
                  />

                  <div>
                    Video <b>{type}</b> you
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="text-sm">
          You can upload more than two videos, but for this demo, CrowdClip&reg;
          will select only one clip with you to personalise the final reel.
          <br />
          Total upload size must be <b>under 100 MB</b>.
        </div>
      </React.Fragment>
    );

  const getPhotoUrls = (isToUpload: boolean): string[] => {
    if (isToUpload) return photoUrl ? [photoUrl] : [];
    return uploadedPhotos.map((photo) => getFileImg(photo));
  };

  const getVideos = (
    isToUpload: boolean,
  ): { id: string; name: string; url: string }[] => {
    if (isToUpload)
      return videoFiles.map((v) => ({
        id: v.uid,
        name: v.name,
        url: URL.createObjectURL(v),
      }));

    return uploadedVideos.map((v) => ({
      id: v.elementId,
      name: v.name,
      url: getFileUrl(v.elementId, v.name),
    }));
  };

  const renderSummaryBlock = (isToUpload: boolean, isTwoCol?: boolean) => {
    const photoUrls = getPhotoUrls(isToUpload);
    const videos = getVideos(isToUpload);

    // show on to-upload block, or on uploaded block when it's the only one
    const showUploadBtn = isToUpload || !isTwoCol;

    const isBtnEnabled =
      (!!photoFile || !isEmpty(videoFiles)) && totalSize <= 100;

    return (
      <div
        className={classNames(
          'flex flex-col gap-4 items-center',
          isTwoCol && {
            'px-4 lg:px-16 py-4 border rounded-md': true,
            'w-full sm:w-1/3': !isToUpload,
            'w-full sm:w-2/3': isToUpload,
          },
        )}
      >
        {isTwoCol && (
          <div className="text-sm italic">
            {isToUpload ? 'Newly uploaded assets' : 'Already uploaded assets'}
          </div>
        )}

        {/* photo */}
        <div className="flex flex-col gap-2 items-center">
          <div className="flex gap-2 items-center">
            <div className="font-semibold">Photo</div>

            {showUploadBtn && (
              <Button
                onClick={() => setStage('photo')}
                variant="link"
                className="h-min text-sm"
              >
                {photoUrl ? 'Change' : 'Upload more'}
              </Button>
            )}
          </div>

          {!isEmpty(photoUrls) ? (
            <div className="flex gap-2">
              {photoUrls.map((url) => (
                <img
                  key={url}
                  src={url}
                  alt="Person Photo"
                  className="w-28 aspect-square rounded-full object-cover"
                />
              ))}
            </div>
          ) : (
            isToUpload && (
              <div className="w-full lg:w-4/5 text-sm">
                No new photos uploaded. We’ll use your existing selfie for this
                analysis. Make sure the person in your photo appears clearly in
                at least one of the videos.
              </div>
            )
          )}
        </div>

        {/* videos */}
        <div className="flex flex-col gap-2 items-center">
          <div className="flex gap-2 items-center">
            <div className="font-semibold">Videos</div>

            {showUploadBtn && (
              <Button
                onClick={() => setStage('video')}
                variant="link"
                className="h-min text-sm"
              >
                Upload more
              </Button>
            )}
          </div>

          {!isEmpty(videos) ? (
            <div className="flex gap-4">
              {videos.map((video) => (
                <div key={video.id} className="w-20">
                  <div className="group relative w-full aspect-[9/16]">
                    {isToUpload && (
                      <div className="absolute w-full h-full flex flex-col items-center justify-center">
                        <SideIcon
                          type="delete"
                          deleteType="clip"
                          onClick={() => handleDeleteVideo(video.id)}
                          hidePopover
                        />
                      </div>
                    )}

                    <video
                      src={video.url}
                      autoPlay
                      muted
                      loop
                      playsInline
                      className="w-full h-full object-cover rounded-md bg-center bg-cover"
                      style={{ backgroundImage: `url(${landscapeOverlay})` }}
                    />
                  </div>

                  <div className="text-xs mt-1 leading-none whitespace-nowrap overflow-hidden text-ellipsis">
                    {video.name}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            isToUpload && (
              <div className="w-full lg:w-4/5 text-sm">
                No new videos uploaded. We’ll use your previously uploaded
                videos for this analysis. Make sure at least one video includes
                the person in your new photo.
              </div>
            )
          )}
        </div>

        {/* alerts */}
        {isToUpload && totalSize > 100 && (
          <Alert
            alertData={{
              variant: 'warning',
              text: 'The total video size is over the 100 MB limit. For this demo, only two short clips are needed. Please remove some videos or upload smaller files to continue.',
            }}
          />
        )}

        {/* upload btn */}
        {showUploadBtn && (
          <>
            <FancyBtn
              text="Start"
              onClick={handleSubmit}
              isDisabled={!isBtnEnabled}
            />

            {isBtnEnabled && (
              <div className="text-sm">
                Once analysis starts, keep this page open while processing runs.
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  if (stage === 'summary')
    return (
      <React.Fragment>
        <ContentHeader
          title={
            hasUploaded && !hasToUpload
              ? 'Update your uploads'
              : 'Ready to find your moment'
          }
        />

        <div className="text-lg">
          {hasUploaded && !hasToUpload ? (
            <>
              We couldn’t find a confident match using the files below.
              <br />
              To help CrowdClip&reg; find you, please{' '}
              <b>add a photo or a video</b> where your face is clearly visible.
            </>
          ) : (
            <>
              We have everything needed for this demo. CrowdClip&reg; will now
              analyse your videos, identify where you appear, and use that
              moment to personalise your highlight reel.
            </>
          )}
        </div>

        <div className="w-full flex flex-col sm:flex-row gap-4 sm:gap-8 justify-center">
          {hasUploaded && renderSummaryBlock(false, hasToUpload)}

          {hasToUpload && renderSummaryBlock(true, hasUploaded)}
        </div>
      </React.Fragment>
    );

  return <></>;
};

export default UploadStep;
